import React from 'react';
import { Link } from 'react-router-dom';
import logo from '../images/new_logo.png';

const Privacy = () => {
  return (
    <div className="legal-page">
      <main className="legal-content">
        <div className="legal-container">
          <h1>Privacy Policy</h1>
          <p className="last-updated">Last updated: {new Date().toLocaleDateString()}</p>

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>BullBearly, LLC ("we," "our," or "us") respects your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our financial information aggregation and analysis platform ("Service").</p>
          </section>

          <section className="legal-section">
            <h2>2. Information We Collect</h2>
            
            <h3>2.1 Personal Information</h3>
            <p>We may collect personal information that you voluntarily provide to us, including:</p>
            <ul>
              <li>Name and contact information (email address, phone number)</li>
              <li>Account credentials and profile information</li>
              <li>Payment information (processed securely through third-party providers)</li>
              <li>Communication preferences</li>
              <li>Customer support interactions</li>
            </ul>

            <h3>2.2 Usage Information</h3>
            <p>We automatically collect certain information when you use our Service:</p>
            <ul>
              <li>Device information (IP address, browser type, operating system)</li>
              <li>Usage patterns and preferences</li>
              <li>RSS feed subscriptions and social media connections</li>
              <li>Custom prompts and analysis preferences</li>
              <li>Log files and analytics data</li>
            </ul>

            <h3>2.3 Third-Party Information</h3>
            <p>We may collect information from third-party sources:</p>
            <ul>
              <li>RSS feeds and news sources you connect</li>
              <li>Social media platforms (with your permission)</li>
              <li>OpenAI API usage data (when using our API key)</li>
              <li>Analytics and performance data</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li>Provide and maintain our Service</li>
              <li>Process your requests and transactions</li>
              <li>Personalize your experience and content</li>
              <li>Analyze financial information using AI technology</li>
              <li>Send you updates, alerts, and communications</li>
              <li>Improve our Service and develop new features</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. Information Sharing and Disclosure</h2>
            
            <h3>4.1 Third-Party Service Providers</h3>
            <p>We may share your information with trusted third-party service providers who assist us in operating our Service:</p>
            <ul>
              <li>OpenAI (for AI analysis services)</li>
              <li>Payment processors (for subscription management)</li>
              <li>Cloud hosting providers (for data storage and processing)</li>
              <li>Analytics services (for usage insights)</li>
              <li>Customer support platforms</li>
            </ul>

            <h3>4.2 Legal Requirements</h3>
            <p>We may disclose your information if required by law or in response to:</p>
            <ul>
              <li>Legal processes or court orders</li>
              <li>Government requests or investigations</li>
              <li>Protection of our rights and property</li>
              <li>Prevention of fraud or illegal activities</li>
              <li>Protection of user safety</li>
            </ul>

            <h3>4.3 Business Transfers</h3>
            <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred to the new entity, subject to the same privacy protections.</p>
          </section>

          <section className="legal-section">
            <h2>5. Data Security</h2>
            <p>We implement appropriate technical and organizational security measures to protect your personal information:</p>
            <ul>
              <li>Encryption of data in transit and at rest</li>
              <li>Secure authentication and access controls</li>
              <li>Regular security audits and updates</li>
              <li>Employee training on data protection</li>
              <li>Incident response procedures</li>
            </ul>
            <p>However, no method of transmission over the internet or electronic storage is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.</p>
          </section>

          <section className="legal-section">
            <h2>6. Data Retention</h2>
            <p>We retain your personal information for as long as necessary to:</p>
            <ul>
              <li>Provide our Service to you</li>
              <li>Comply with legal obligations</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Improve our Service and analytics</li>
            </ul>
            <p>When you delete your account, we will delete or anonymize your personal information, except where retention is required by law.</p>
          </section>

          <section className="legal-section">
            <h2>7. Your Rights and Choices</h2>
            <p>You have certain rights regarding your personal information:</p>
            <ul>
              <li><strong>Access:</strong> Request access to your personal information</li>
              <li><strong>Correction:</strong> Request correction of inaccurate information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Portability:</strong> Request a copy of your data in a portable format</li>
              <li><strong>Opt-out:</strong> Unsubscribe from marketing communications</li>
              <li><strong>Data Processing:</strong> Object to certain processing activities</li>
            </ul>
            <p>To exercise these rights, please contact us at privacy@bullbearly.com.</p>
          </section>

          <section className="legal-section">
            <h2>8. Cookies and Tracking Technologies</h2>
            <p>We use cookies and similar tracking technologies to:</p>
            <ul>
              <li>Remember your preferences and settings</li>
              <li>Analyze usage patterns and performance</li>
              <li>Provide personalized content</li>
              <li>Ensure security and prevent fraud</li>
            </ul>
            <p>You can control cookie settings through your browser preferences, but disabling cookies may affect Service functionality.</p>
          </section>

          <section className="legal-section">
            <h2>9. International Data Transfers</h2>
            <p>Your information may be transferred to and processed in countries other than your country of residence. We ensure appropriate safeguards are in place for such transfers, including:</p>
            <ul>
              <li>Standard contractual clauses</li>
              <li>Adequacy decisions by relevant authorities</li>
              <li>Other appropriate safeguards as required by law</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>10. Children's Privacy</h2>
            <p>Our Service is not intended for children under 13 years of age. We do not knowingly collect personal information from children under 13. If we become aware that we have collected personal information from a child under 13, we will take steps to delete such information.</p>
          </section>

          <section className="legal-section">
            <h2>11. California Privacy Rights</h2>
            <p>If you are a California resident, you have additional rights under the California Consumer Privacy Act (CCPA):</p>
            <ul>
              <li>Right to know what personal information is collected</li>
              <li>Right to delete personal information</li>
              <li>Right to opt-out of the sale of personal information</li>
              <li>Right to non-discrimination for exercising privacy rights</li>
            </ul>
            <p>We do not sell personal information to third parties.</p>
          </section>

          <section className="legal-section">
            <h2>12. European Privacy Rights</h2>
            <p>If you are in the European Economic Area (EEA), you have additional rights under the General Data Protection Regulation (GDPR), including the right to lodge a complaint with a supervisory authority.</p>
          </section>

          <section className="legal-section">
            <h2>13. Changes to This Privacy Policy</h2>
            <p>We may update this Privacy Policy from time to time. We will notify you of any material changes by:</p>
            <ul>
              <li>Posting the updated policy on our website</li>
              <li>Sending email notifications to registered users</li>
              <li>Providing in-app notifications</li>
            </ul>
            <p>Your continued use of the Service after changes become effective constitutes acceptance of the updated Privacy Policy.</p>
          </section>

          <section className="legal-section">
            <h2>14. Contact Information</h2>
            <p>If you have any questions about this Privacy Policy or our privacy practices, please contact us at:</p>
            <p>
              BullBearly, LLC<br />
              Privacy Officer<br />
              Email: privacy@bullbearly.com<br />
              Website: https://bullbearly.com
            </p>
          </section>
        </div>
      </main>

      <footer className="footer">
        <div className="footer-container">
          <div className="footer-content">
            <div className="footer-brand">
              <img src={logo} alt="BullBearly" className="footer-logo" />
              <span className="footer-brand-text">BullBearly</span>
            </div>
            <div className="footer-links">
              <Link to="/terms" className="footer-link">Terms of Service</Link>
              <Link to="/privacy" className="footer-link">Privacy Policy</Link>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 BullBearly, LLC. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
